import _ from 'lodash'
import { APIServer } from '@/app/data/api'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { SQL } from '@/lib/Sqlite'
import { prepareSQLiteValue } from '@/lib/Sqlite/schema'
import { parseRow, sortByKeys } from '@/app/util'
import { Network } from '@capacitor/network'
import { syncQueue, JobPriority } from '@/app/sync'
import { SyncManager } from '@/app/sync/syncManager'
import type { FieldsUpdatedAt, Floorplan, ResResult } from '@/app/data/types'
import type { SyncAction } from '@/lib/Firebase/types'
import { GCS_ROOT_PATH, isApp } from '../../..'
import SQLite from '../../../lib/Task/sqlite'
import Merge from '../../../lib/Task/merge'
import { useAllTaskStore } from '../../store/task'
import { CloudFile } from '../../../lib/CloudFile'
import { getImageSize } from '@/lib/Functions/files'
import { SQLiteEventBus, SQLiteUpdateEvent } from '@/lib/sqliteEventBus'
import { FileDownloadManager } from '../../../lib/Task/fileDownloadManager'

const storeKey = 'floorplans'

export const useFloorplanStore = defineStore('floorplans', () => {
  const floorplans = ref<Floorplan[]>([])
  const currentTaskDatabaseId = ref<string>('')

  // SQLite 이벤트 리스너 참조 (중복 등록 방지 및 cleanup용)
  let currentListener: ((event: SQLiteUpdateEvent) => void) | null = null

  const notUploadedFloorplans = computed(() => {
    return floorplans.value.filter(item => item.modifiedFromLocal)
  })
  async function uploadNotUploaded(): Promise<void> {
    if (notUploadedFloorplans.value.length > 0) {
      await requestUpdate(notUploadedFloorplans.value)
    }
  }
  async function individualSetLocalModified(
    localStringId: string, // 시그니처 변경
    keyValues: { key: string; value: unknown }[] = [],
  ): Promise<void> {
    const db = await SQL.getDatabase()
    let sql = `UPDATE ${storeKey} SET `
    for (let i = 0; i < keyValues.length; i++) {
      const keyValue = keyValues[i]
      sql += `${keyValue.key} = ?`
      if (i < keyValues.length - 1) {
        sql += ', '
      }
    }

    sql += ' WHERE localStringId = ?' // WHERE 절 변경
    // 값 정규화: iOS native bridge가 number를 TEXT 컬럼에 "557.0"으로 저장하는 것을 차단
    await db.run(sql, [...keyValues.map(item => prepareSQLiteValue(item.value)), localStringId])

    // 이벤트 발행 (리스너 완료 대기)
    await SQLiteEventBus.getInstance().emitUpdate('floorplans', 'update', [localStringId])
  }

  /**
   * SQLite 이벤트 리스너 설정
   * @param taskId - 현재 task ID
   */
  function setupSQLiteListener(taskId: string): void {
    // 기존 리스너 해제 (중복 등록 방지)
    if (currentListener) {
      SQLiteEventBus.getInstance().offTableUpdated('floorplans', currentListener)
    }

    currentListener = async (event: SQLiteUpdateEvent): Promise<void> => {
      try {
        const db = await SQL.getDatabase()

        if (event.type === 'bulk' || event.ids.length === 0) {
          // 대량 업데이트: 전체 로드
          const localData = await db.query(`SELECT * FROM ${storeKey} WHERE task_id = ?`, [taskId])
          floorplans.value = localData.values?.map((row: unknown) => parseRow(row)) as Floorplan[]
        } else {
          // 개별 업데이트: 해당 항목만 갱신
          for (const id of event.ids) {
            const result = await db.query(
              `SELECT * FROM ${storeKey} WHERE localStringId = ? AND task_id = ?`,
              [id, taskId],
            )

            if (result.values && result.values.length > 0) {
              const updatedItem = parseRow(result.values[0]) as Floorplan
              const index = floorplans.value.findIndex(f => f.localStringId === id)

              if (updatedItem.deleted_at) {
                // 삭제된 항목은 배열에서 제거
                if (index !== -1) floorplans.value.splice(index, 1)
              } else if (index !== -1) {
                // 기존 항목 업데이트
                floorplans.value[index] = updatedItem
              } else {
                // 새 항목 추가
                floorplans.value.push(updatedItem)
              }
            } else {
              // 쿼리 결과 없음 = 삭제됨
              const index = floorplans.value.findIndex(f => f.localStringId === id)
              if (index !== -1) floorplans.value.splice(index, 1)
            }
          }
        }
      } catch (error) {
        console.error('[FloorplanStore] Failed to process SQLite event:', error)
      }
    }

    SQLiteEventBus.getInstance().onTableUpdated('floorplans', currentListener)
  }

  async function getServerData(taskId: string, floorList?: Floorplan[]): Promise<Floorplan[]> {
    if (!taskId) return []
    if (floorList) {
      return floorList.map(item => {
        return {
          ...item,
          localStringId: String(item.id),
          modifiedFromLocal: false,
        } as Floorplan
      })
    }
    try {
      const data: Floorplan[] = await APIServer.getFloorplanByTaskId(taskId)
      return data.map(item => {
        return {
          ...item,
          localStringId: String(item.id),
          modifiedFromLocal: false,
        } as Floorplan
      })
    } catch (error) {
      console.error('Error getting server:', error)
      throw error
    }
  }

  const addApiQueue = async (
    itemsToProcess: { item: Floorplan; itemId: string }[],
  ): Promise<void> => {
    if (itemsToProcess.length === 0) return

    for (const { item } of itemsToProcess) {
      // 로컬에서 변경된 항목만 동기화 대상으로 삼습니다.
      if (!item.modifiedFromLocal) continue
      addFloorplanJobToQueue(item)
    }
  }

  const requestUpdate = async (datas: Floorplan[]): Promise<void> => {
    const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(datas, storeKey)
    await SQLite.updateSqliteOnly(itemsToProcess, columns, currentTaskDatabaseId.value, storeKey)
    await addApiQueue(itemsToProcess as { item: Floorplan; itemId: string }[])
  }

  /**
   * Floorplan 동기화 작업을 SyncQueue에 추가하는 헬퍼 함수
   * @param item 동기화할 Floorplan 데이터
   */
  async function addFloorplanJobToQueue(item: Floorplan): Promise<void> {
    const executeCallback = async (data: Partial<Floorplan>): Promise<ResResult[]> => {
      try {
        // fields_updated_at에 기록된 변경 필드만 전송하여
        // stale 데이터(예: 이전 file_url)가 서버의 최신 값을 덮어쓰는 것을 방지
        const updatePayload: Record<string, unknown> = { id: Number(data.id) }
        let fieldsUpdatedAt: Record<string, string> = {}
        if (typeof data.fields_updated_at === 'string') {
          try {
            fieldsUpdatedAt = JSON.parse(data.fields_updated_at)
          } catch {
            console.warn(`[FloorplanStore] Invalid fields_updated_at JSON for floorplan ${data.id}`)
          }
        } else {
          fieldsUpdatedAt = data.fields_updated_at || {}
        }
        const dataRecord = data as Record<string, unknown>

        for (const field of Object.keys(fieldsUpdatedAt)) {
          if (field in dataRecord && field !== 'id' && field !== 'width' && field !== 'height') {
            updatePayload[field] = dataRecord[field]
          }
        }
        updatePayload.fields_updated_at = data.fields_updated_at

        if (data.deleted_at) {
          updatePayload.deleted_at = data.deleted_at
        }

        const result = await APIServer.updateFloorplan([updatePayload])

        // API 응답 검증
        if (!result || !Array.isArray(result) || result.length === 0) {
          throw new Error(`Floorplan update failed: Invalid API response for id ${data.id}`)
        }

        return result
      } catch (error) {
        console.error(`[FloorplanStore] API call failed for floorplan ${data.id}:`, error)
        throw error
      }
    }

    syncQueue.addJob({
      localStringId: item.localStringId as string,
      data: item as Floorplan,
      priority: JobPriority.NORMAL,
      maxRetries: 5,
      tag: 'floorplans',
      execute: executeCallback,

      onSuccess: async (_, data) => {
        await individualSetLocalModified(data.localStringId as string, [
          { key: 'modifiedFromLocal', value: false },
        ])
      },

      onError: (error, data) => {
        console.error(`[SyncQueue] Floorplan job for ${data.localStringId} FAILED.`, error)
      },
    })
  }

  const computedFloorplans = computed<(facilityId: string) => Floorplan[]>(() => {
    return (facilityId: string): Floorplan[] => {
      return sortByKeys(
        floorplans.value.filter(
          item => item.facility_id.toString() == facilityId && !item.deleted_at,
        ),
      )
    }
  })

  function getFloorplans(facilityId: string): Floorplan[] {
    return sortByKeys(
      floorplans.value.filter(
        item => item.facility_id.toString() == facilityId && !item.deleted_at,
      ),
    )
  }

  async function initData(taskId: string, floorList?: Floorplan[]): Promise<void> {
    currentTaskDatabaseId.value = taskId
    let localData: Floorplan[] = []
    let mergedData: Floorplan[] = []
    const networkStatus = await Network.getStatus()
    if (isApp()) {
      localData = (await SQLite.getLocalData(storeKey, 'task_id', taskId, true)) as Floorplan[]
      // 초기값은 로컬 데이터
      mergedData = localData
      setupSQLiteListener(taskId)
    }
    if (networkStatus.connected) {
      return await getServerData(taskId, floorList)
        .then(async serverData => {
          if (!isApp()) {
            return Merge.sortupdatedAt(serverData)
          }
          // 3. 테이블 생성 처리
          if (!(await SQLite.checkTableExists(storeKey, serverData))) {
            return serverData
          }

          return serverData
        })
        .then(async serverData => {
          if (!isApp()) {
            return serverData
          }
          // 서버가 빈 배열인데 로컬에 비삭제 데이터가 있으면 merge 스킵 (데이터 소실 방지)
          const activeLocalData = localData.filter(item => !item.deleted_at)
          if (serverData.length > 0 || activeLocalData.length === 0) {
            mergedData = Merge.mergeBothData(localData, serverData, storeKey)
          }
          return mergedData
        })
        .then(async mergedData => {
          // 5. 스토어 업데이트
          // initData에서는 store가 이미 세팅되었으므로 emitUpdate와 addApiQueue를 생략한다
          if (mergedData.length > 0) {
            floorplans.value = mergedData
            if (!isApp()) return
            const isAlreadySyncing = SyncManager.isTaskSyncing(taskId)
            if (!isAlreadySyncing) {
              const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(
                mergedData,
                storeKey,
              )
              await SQLite.updateSqliteOnly(
                itemsToProcess,
                columns,
                currentTaskDatabaseId.value,
                storeKey,
                true,
              )
            } else {
              console.info(
                `[FloorplanStore] taskId ${taskId}: backgroundSync 진행 중 - requestUpdate 스킵 (중복 방지)`,
              )
            }
          }
        })
        .catch(error => {
          throw error
        })
    } else {
      const sortData = Merge.sortupdatedAt(mergedData)
      floorplans.value = sortData
    }
  }

  async function updateFloorplan(floorplan: Partial<Floorplan>): Promise<void> {
    // 단일 업데이트는 배열 함수로 위임
    await updateFloorplans([floorplan])
  }
  async function updateFloorplans(paramFloorplans: Partial<Floorplan>[]): Promise<void> {
    const needUpdateFloorplans: Partial<Floorplan>[] = []
    const mergedFloorplans: Floorplan[] = []

    for (const paramFloorplan of paramFloorplans) {
      const currentMetaData = floorplans.value.find(x => x.id == paramFloorplan.id)
      if (!currentMetaData) continue
      const changedFields = Merge.findChangedField(currentMetaData, paramFloorplan)
      const mergedFloorplan = {
        ...currentMetaData,
        ...paramFloorplan,
        id: Number(paramFloorplan.id),
        fields_updated_at: changedFields,
      } as Floorplan
      if (isApp()) {
        mergedFloorplan.modifiedFromLocal = true
      }
      mergedFloorplans.push(mergedFloorplan)
      needUpdateFloorplans.push(paramFloorplan)
    }

    // 서버 업데이트
    if (isApp()) {
      await requestUpdate(mergedFloorplans)
    } else {
      await APIServer.updateFloorplan(needUpdateFloorplans)
    }

    // floorplans.value 즉시 업데이트 (앱/웹 공통 — 앱에서도 SQLiteEventBus 대기 없이 반영)
    for (const merged of mergedFloorplans) {
      const index = floorplans.value.findIndex(x => x.id == merged.id)
      if (index !== -1) {
        floorplans.value[index] = merged
      }
    }
  }

  function getCurrentFloorplan(floorplanId: string): Floorplan | null {
    return floorplans.value.find(x => x.id.toString() == floorplanId) || null
  }

  async function deleteFloorplans(floorplanIds: string[]): Promise<void> {
    const findFloorplans = floorplans.value.filter(item =>
      floorplanIds.includes(item.id.toString()),
    )
    if (findFloorplans.length === 0) return

    if (isApp()) {
      const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(
        findFloorplans as unknown as Floorplan[],
        storeKey,
      )
      await SQLite.updateSqliteOnly(itemsToProcess, columns, currentTaskDatabaseId.value, storeKey)
    }

    await APIServer.deleteFloorplans(
      findFloorplans.map(item => {
        return {
          id: Number(item.id),
        }
      }),
    )

    if (!isApp()) {
      floorplans.value = floorplans.value.map(item => {
        if (floorplanIds.includes(item.id.toString())) {
          return { ...item, deleted_at: Merge.getCurrentTime() }
        }
        return item
      })
    }
  }

  function getFloorplanName(floorplanId: string): string {
    return floorplans.value.find(x => x.id.toString() == floorplanId)?.name ?? ''
  }

  async function addFloorplanByFile(
    facilityId: string,
    newFloorplanInfo: Partial<Floorplan>,
  ): Promise<Floorplan> {
    const allTaskStore = useAllTaskStore()
    const taskId = allTaskStore.currentTaskData?.id
    if (!taskId) throw new Error('taskId is required')
    const addedInfo: Partial<Floorplan> = {
      name: newFloorplanInfo.name,
      facility_id: Number(facilityId),
    }
    if (newFloorplanInfo.sort_key) {
      addedInfo.sort_key = newFloorplanInfo.sort_key
    }
    const [addedFloorplan] = await APIServer.addFloorplans([addedInfo])
    let resourcePath = ''

    if (newFloorplanInfo.needUploadFile) {
      const timestamp = Date.now()
      const originalFileName = newFloorplanInfo.needUploadFile.name
        .split('.')
        .slice(0, -1)
        .join('.')
      const extension = newFloorplanInfo.needUploadFile.name.split('.').pop()
      const imageSize = await getImageSize(newFloorplanInfo.needUploadFile)

      resourcePath = await CloudFile.uploadFile(
        'homecheck-service',
        `${GCS_ROOT_PATH}/task/${taskId}/facility_branch/${facilityId}/floor_plan/${addedFloorplan.id}/${originalFileName}_${timestamp}.${extension}`,
        newFloorplanInfo.needUploadFile,
      )

      const originFileUrl = newFloorplanInfo.originFileUrl || resourcePath

      const [updatedFloorplan] = await APIServer.updateFloorplan([
        {
          id: addedFloorplan.id,
          file_url: resourcePath,
          origin_file_url: originFileUrl,
          ...imageSize, // width, height
        },
      ])

      resourcePath = updatedFloorplan.file_url

      return {
        ...updatedFloorplan,
        file_url: resourcePath,
        localStringId: String(updatedFloorplan.id),
      }
    }

    return {
      ...addedFloorplan,
      file_url: resourcePath,
      localStringId: String(addedFloorplan.id),
    }
  }

  /**
   * 도면을 복제하는 함수
   * - 서버에서 복제 후 로컬 store에 추가
   * - 중복 ID 체크를 통해 이미 존재하는 도면은 스킵
   */
  async function cloneFloorplans(targetFloorplans: Partial<Floorplan>[]): Promise<Floorplan[]> {
    if (targetFloorplans.length === 0) return []

    const newFloorplans = await Promise.all(
      targetFloorplans.map(async item => {
        const [newFloorplan] = await APIServer.cloneFloorplans([
          {
            id: Number(item.id),
            name: `${item.name} 복사본`,
            facility_id: Number(item.facility_id),
            file_url: item.file_url,
            origin_file_url: item.origin_file_url,
          },
        ])
        return newFloorplan
      }),
    )

    // 중복 체크 후 추가 (이미 존재하는 ID는 스킵)
    const existingIds = new Set(floorplans.value.map(f => f.id))
    const uniqueNewFloorplans = newFloorplans.filter(f => !existingIds.has(f.id))

    if (uniqueNewFloorplans.length > 0) {
      if (isApp()) {
        const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(
          uniqueNewFloorplans as unknown as Floorplan[],
          storeKey,
        )
        await SQLite.updateSqliteOnly(
          itemsToProcess,
          columns,
          currentTaskDatabaseId.value,
          storeKey,
        )
      }

      floorplans.value = [...floorplans.value, ...uniqueNewFloorplans]
    }

    return newFloorplans
  }

  // 도면 + 하위 데이터(결함, 사진, 댓글) 전체 복제
  async function cloneFloorplansWithData(
    targetFloorplans: Partial<Floorplan>[],
  ): Promise<Floorplan[]> {
    if (targetFloorplans.length === 0) return []

    const newFloorplans = await Promise.all(
      targetFloorplans.map(async item => {
        const [newFloorplan] = await APIServer.cloneFloorplans([
          {
            id: Number(item.id),
            name: `${item.name} 복사본`,
            include_data: true,
          } as Partial<Floorplan> & { include_data: boolean },
        ])
        return newFloorplan
      }),
    )

    const existingIds = new Set(floorplans.value.map(f => f.id))
    const uniqueNewFloorplans = newFloorplans.filter(f => !existingIds.has(f.id))

    if (uniqueNewFloorplans.length > 0) {
      if (isApp()) {
        const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(
          uniqueNewFloorplans as unknown as Floorplan[],
          storeKey,
        )
        await SQLite.updateSqliteOnly(
          itemsToProcess,
          columns,
          currentTaskDatabaseId.value,
          storeKey,
        )
      }

      floorplans.value = [...floorplans.value, ...uniqueNewFloorplans]
    }

    return newFloorplans
  }

  function allClear(): void {
    // 리스너 해제
    if (currentListener) {
      SQLiteEventBus.getInstance().offTableUpdated('floorplans', currentListener)
      currentListener = null
    }
    floorplans.value = []
  }

  async function getAllModifiedData(): Promise<Floorplan[]> {
    const db = await SQL.getDatabase()
    const localData = await db.query(`SELECT * FROM ${storeKey} WHERE modifiedFromLocal = 1`)
    return localData.values?.map((row: unknown) => parseRow(row)) as Floorplan[]
  }

  /**
   * 외부에서 floorplan이 추가될 때 호출
   * - 중복 ID 체크를 통해 이미 존재하는 도면은 스킵하거나 병합
   * @param newFloorplans 추가할 도면 배열
   * @param isUpdate true면 기존 데이터 업데이트, false면 신규 추가
   */
  async function addFloorStore(
    newFloorplans: Floorplan[],
    isUpdate: boolean = false,
  ): Promise<void> {
    const actuallyAdded: Floorplan[] = []

    for (const floorplan of newFloorplans) {
      const existingIndex = floorplans.value.findIndex(item => item.id == floorplan.id)

      if (existingIndex !== -1) {
        // 이미 존재하는 경우
        if (isUpdate) {
          // 업데이트 모드: 기존 데이터와 병합
          floorplans.value[existingIndex] = {
            ...floorplans.value[existingIndex],
            ...floorplan,
          }
          actuallyAdded.push(floorplans.value[existingIndex])
        } else {
          // SSE INSERT가 file_url 없는 불완전 데이터를 먼저 추가한 경우 대응:
          // 핵심 필드가 더 완전하면 병합하여 정확한 데이터 보존
          const existing = floorplans.value[existingIndex]
          const shouldMerge =
            (floorplan.file_url && !existing.file_url) ||
            (floorplan.file_url && floorplan.file_url !== existing.file_url) ||
            (floorplan.origin_file_url && !existing.origin_file_url) ||
            (floorplan.width && !existing.width) ||
            (floorplan.height && !existing.height)

          if (shouldMerge) {
            floorplans.value[existingIndex] = { ...existing, ...floorplan }
            actuallyAdded.push(floorplans.value[existingIndex])
          }
        }
      } else {
        // 존재하지 않으면 추가
        floorplans.value.push(floorplan)
        actuallyAdded.push(floorplan)
      }
    }

    // 실제로 추가/업데이트된 항목만 SQLite에 반영
    if (isApp() && actuallyAdded.length > 0) {
      const forLocalFloorplans = actuallyAdded.map(item => {
        return {
          ...item,
          localStringId: String(item.id),
          modifiedFromLocal: false,
        }
      })
      const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(
        forLocalFloorplans as unknown as Floorplan[],
        storeKey,
      )
      await SQLite.updateSqliteOnly(itemsToProcess, columns, currentTaskDatabaseId.value, storeKey)
    }
  }

  //외부에서 floorplan이 수정될때
  async function updateFloorStore(newFloorplan: Floorplan): Promise<void> {
    const findFloorplan = floorplans.value.find(item => item.id == newFloorplan.id)
    if (!findFloorplan) return
    if (!isApp()) {
      floorplans.value = floorplans.value.map(item => {
        if (item.id == newFloorplan.id) {
          return newFloorplan
        }
        return item
      })
    }

    if (isApp()) {
      const forLocalFloorplans = { ...newFloorplan }
      forLocalFloorplans.localStringId = newFloorplan.id.toString()
      forLocalFloorplans.modifiedFromLocal = false
      const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(
        [forLocalFloorplans as unknown as Floorplan],
        storeKey,
      )
      await SQLite.updateSqliteOnly(itemsToProcess, columns, currentTaskDatabaseId.value, storeKey)
    }
  }

  /**
   * SSE를 통해 수신된 Floorplan 데이터를 처리하는 함수
   * - 로컬에서 수정 중인 데이터(modifiedFromLocal=true)는 보호
   * - updated_at 타임스탬프 기반으로 최신 데이터 판단
   * - 앱 환경: SQLite에만 저장 → 리스너가 메모리 업데이트 (SSoT 패턴)
   * - 웹 환경: 메모리 직접 업데이트
   */
  async function handleRealtimeUpdateFloorplan(
    action: SyncAction,
    dataList: Partial<Floorplan>[],
  ): Promise<void> {
    const allTaskStore = useAllTaskStore()
    const currentTaskData = allTaskStore.currentTaskData
    if (!currentTaskData) return

    const numericTaskId = Number(currentTaskData.id)
    if (!dataList || dataList.length === 0) return

    // 1. 현재 활성화된 과업(Task)에 해당하는 도면만 필터링
    const filteredDataList = dataList.filter(x => Number(x.task_id) == numericTaskId)
    if (filteredDataList.length === 0) return

    // 두 타임스탬프 비교 (Firebase 데이터가 더 최신인지 확인)
    const isNewerTimestamp = (dataTimestamp?: string, existingTimestamp?: string): boolean => {
      if (!dataTimestamp) return false
      if (!existingTimestamp) return true
      return new Date(dataTimestamp).getTime() > new Date(existingTimestamp).getTime()
    }

    // data에 업데이트할 필드가 하나라도 있는지 확인
    const hasNewerFields = (
      dataFieldsUpdatedAt?: FieldsUpdatedAt,
      existingFieldsUpdatedAt?: FieldsUpdatedAt,
    ): boolean => {
      if (!dataFieldsUpdatedAt) return false
      if (!existingFieldsUpdatedAt) return true

      for (const [field, dataTimestamp] of Object.entries(dataFieldsUpdatedAt)) {
        const existingTimestamp = existingFieldsUpdatedAt[field]
        // existing에 해당 필드가 없거나, data가 더 최신이면 업데이트 필요
        if (!existingTimestamp || isNewerTimestamp(dataTimestamp, existingTimestamp)) {
          return true
        }
      }
      return false
    }

    // 필드별 타임스탬프 비교하여 최신 값만 선택하여 병합
    const mergeByFieldTimestamp = (
      data: Partial<Floorplan>,
      existing: Floorplan,
    ): Partial<Floorplan> => {
      const dataFieldsUpdatedAt = data.fields_updated_at || {}
      const existingFieldsUpdatedAt = existing.fields_updated_at || {}

      const mergedData: Partial<Floorplan> = {}
      const mergedFieldsUpdatedAt: FieldsUpdatedAt = { ...existingFieldsUpdatedAt }

      // data의 모든 필드에 대해 타임스탬프 비교
      for (const [field, dataTimestamp] of Object.entries(dataFieldsUpdatedAt)) {
        const existingTimestamp = existingFieldsUpdatedAt[field]

        // existing에 해당 필드 타임스탬프가 없거나, data가 더 최신이면 data 값 사용
        if (!existingTimestamp || isNewerTimestamp(dataTimestamp, existingTimestamp)) {
          // 해당 필드 값을 data에서 가져옴
          if (field in data) {
            const dataRecord = data as Record<string, unknown>
            const mergedRecord = mergedData as Record<string, unknown>
            mergedRecord[field] = dataRecord[field]
          }
          mergedFieldsUpdatedAt[field] = dataTimestamp
        }
      }

      mergedData.fields_updated_at = mergedFieldsUpdatedAt
      return mergedData
    }

    // 서버 데이터를 Store 규격(Floorplan)으로 변환하는 헬퍼 함수
    const transformToStoreFloorplan = (
      data: Partial<Floorplan>,
      existing?: Floorplan,
    ): Floorplan => {
      if (existing) {
        // 기존 데이터가 있으면 필드별 타임스탬프 비교하여 병합
        const mergedFields = mergeByFieldTimestamp(data, existing)
        return {
          ...existing,
          ...mergedFields,
          id: Number(data.id),
          facility_id: Number(data.facility_id ?? existing.facility_id),
          task_id: Number(data.task_id ?? existing.task_id),
          localStringId: data.id?.toString() ?? existing.localStringId ?? '',
          modifiedFromLocal: false, // Firebase 데이터는 서버 기준이므로 false
          deleted_at: data.deleted_at ?? existing.deleted_at ?? null,
        } as Floorplan
      }

      // 기존 데이터가 없으면 그대로 사용 (새 데이터)
      return {
        ...data,
        id: Number(data.id),
        facility_id: Number(data.facility_id),
        task_id: Number(data.task_id),
        localStringId: data.id?.toString() ?? '',
        modifiedFromLocal: false,
        deleted_at: data.deleted_at ?? null,
      } as Floorplan
    }

    // 배치 다운로드할 파일 URL 수집 배열
    const pendingDownloadUrls: string[] = []

    /**
     * 다운로드할 파일 URL을 배치 큐에 추가
     * @param fileUrl 다운로드할 파일 URL
     */
    const addToPendingDownloads = (fileUrl: string): void => {
      if (!fileUrl || !isApp()) return
      // 중복 방지
      if (!pendingDownloadUrls.includes(fileUrl)) {
        pendingDownloadUrls.push(fileUrl)
      }
    }

    /**
     * 수집된 파일들을 배치로 다운로드 실행
     */
    const executeBatchDownload = async (): Promise<void> => {
      if (pendingDownloadUrls.length === 0 || !isApp()) return

      const fileManager = FileDownloadManager.getInstance()
      const taskId = currentTaskData.id.toString()

      await fileManager.addDownloadBatch({
        taskId,
        tasks: pendingDownloadUrls.map(fileUrl => ({
          taskId,
          fileUrl,
          name: fileUrl,
        })),
        type: 'file',
        sectionId: 'floorplan',
      })
    }

    let isChanged = false
    const changedFloorplans: Floorplan[] = []

    for (const data of filteredDataList) {
      const numericId = Number(data.id)
      const index = floorplans.value.findIndex(f => f.id == numericId)

      if (action === 'INSERT') {
        if (index === -1) {
          // 신규 데이터 추가
          const newFloorplan = transformToStoreFloorplan(data)
          // 파일 다운로드 URL 수집 (배치 처리) - 앱 환경만
          if (newFloorplan.file_url) {
            addToPendingDownloads(newFloorplan.file_url)
          }
          // 메모리 직접 업데이트 (앱/웹 공통 - Firebase SSE 데이터는 서버 최신 데이터)
          floorplans.value.push(newFloorplan)
          changedFloorplans.push(newFloorplan)
          isChanged = true
        } else {
          // 이미 존재하는 경우 (로컬에서 먼저 추가됨)
          const existing = floorplans.value[index]

          // 로컬에서 수정 중인 데이터는 보호 (동기화 큐에서 처리 중)
          if (existing.modifiedFromLocal) {
            continue
          }

          // Firebase 데이터에 최신 필드가 하나라도 있는 경우에만 병합
          if (hasNewerFields(data.fields_updated_at, existing.fields_updated_at)) {
            const merged = transformToStoreFloorplan(data, existing)
            // 파일 URL이 변경된 경우에만 다운로드 URL 수집 - 앱 환경만
            if (merged.file_url && merged.file_url !== existing.file_url) {
              addToPendingDownloads(merged.file_url)
            }
            // 메모리 직접 업데이트 (앱/웹 공통)
            floorplans.value[index] = merged
            changedFloorplans.push(merged)
            isChanged = true
          }
          // Firebase 데이터에 최신 필드가 없으면 스킵
        }
      } else if (action === 'UPDATE') {
        if (index !== -1) {
          const existing = floorplans.value[index]

          // 로컬에서 수정 중인 데이터는 보호
          if (existing.modifiedFromLocal) {
            continue
          }

          // Firebase 데이터에 최신 필드가 하나라도 있는 경우에만 업데이트
          if (hasNewerFields(data.fields_updated_at, existing.fields_updated_at)) {
            const merged = transformToStoreFloorplan(data, existing)
            // 파일 URL이 변경된 경우에만 다운로드 URL 수집 - 앱 환경만
            if (merged.file_url && merged.file_url !== existing.file_url) {
              addToPendingDownloads(merged.file_url)
            }
            // 메모리 직접 업데이트 (앱/웹 공통)
            floorplans.value[index] = merged
            changedFloorplans.push(merged)
            isChanged = true
          }
          // Firebase 데이터에 최신 필드가 없으면 스킵
        }
      } else if (action === 'DELETE') {
        if (index !== -1) {
          const existing = floorplans.value[index]

          // 이미 삭제 처리된 경우 스킵
          if (existing.deleted_at) {
            continue
          }

          // deleted_at 마킹
          const deletedFloorplan = {
            ...existing,
            deleted_at: data.deleted_at ?? Merge.getCurrentTime(),
          }
          // 메모리 직접 업데이트 (앱/웹 공통)
          floorplans.value[index] = deletedFloorplan
          changedFloorplans.push(deletedFloorplan)
          isChanged = true
        }
      }
    }

    // 2. 변경사항이 있을 경우 후속 처리
    if (isChanged) {
      // 메모리 재할당으로 반응성 트리거 (앱/웹 공통)
      floorplans.value = [...floorplans.value]

      if (isApp() && changedFloorplans.length > 0) {
        // 앱 환경: SQLite에도 저장 (영속성)
        const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(
          changedFloorplans,
          storeKey,
        )
        await SQLite.updateSqliteOnly(
          itemsToProcess,
          columns,
          currentTaskDatabaseId.value,
          storeKey,
        )
      }

      // 3. 수집된 파일들 배치 다운로드 실행 (앱 환경만)
      await executeBatchDownload()
    }
  }
  return {
    initData,
    uploadNotUploaded,
    requestUpdate,
    notUploadedFloorplans,
    floorplans,
    allClear,
    getAllModifiedData,
    addApiQueue,
    getFloorplans,
    computedFloorplans,
    updateFloorplan,
    updateFloorplans,
    deleteFloorplans,
    cloneFloorplans,
    cloneFloorplansWithData,
    addFloorplanByFile,
    addFloorStore,
    updateFloorStore,
    handleRealtimeUpdateFloorplan,
    getCurrentFloorplan,
    getFloorplanName,
  }
})
